import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { executeJsSandbox } from '../lib/codeRunner';
import { playSound } from '../lib/soundEffects';

const { FiCode, FiPlay, FiCheck, FiCopy, FiDownload, FiX, FiTerminal, FiTrash2 } = FiIcons;

export default function CodeRunnerModal({
  isOpen,
  onClose,
  initialCode = '',
  title = 'Code Snippet Runner',
  language = 'javascript',
}) {
  const [code, setCode] = useState(initialCode);
  const [output, setOutput] = useState([]);
  const [duration, setDuration] = useState(null);
  const [copied, setCopied] = useState(false);
  const [status, setStatus] = useState('idle'); // 'idle' | 'running' | 'success' | 'error'

  useEffect(() => {
    if (initialCode) {
      setCode(initialCode);
    }
  }, [initialCode]);

  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  const handleRun = () => {
    playSound('click');
    setStatus('running');
    setTimeout(() => {
      const res = executeJsSandbox(code);
      setOutput(res.logs);
      setDuration(res.duration);
      setStatus(res.success ? 'success' : 'error');
      playSound(res.success ? 'launch' : 'lock');
    }, 50);
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    playSound('copy');
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDownload = () => {
    const blob = new Blob([code], { type: 'text/javascript' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `snippet-${Date.now()}.js`;
    a.click();
    URL.revokeObjectURL(url);
    playSound('launch');
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="fixed inset-0 bg-black/75 backdrop-blur-md"
        />

        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 15 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 15 }}
          transition={{ duration: 0.2 }}
          className="relative w-full max-w-2xl rounded-2xl bg-[#121216] border border-[#26262e] shadow-2xl p-6 z-10 max-h-[90vh] flex flex-col font-mono"
        >
          {/* Header */}
          <div className="flex items-center justify-between pb-3 border-b border-[#24242c] shrink-0">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center text-emerald-400">
                <SafeIcon icon={FiCode} className="w-5 h-5" />
              </div>
              <div>
                <h3 className="text-base font-bold text-white font-sans">{title}</h3>
                <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                  {language} sandbox
                </span>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={handleCopy}
                className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs text-[#aaa] hover:text-white bg-[#1a1a22] hover:bg-[#252530] border border-[#333] rounded-lg transition-colors"
                title="Copy code"
              >
                <SafeIcon icon={copied ? FiCheck : FiCopy} className={`w-3.5 h-3.5 ${copied ? 'text-emerald-400' : ''}`} />
                <span>{copied ? 'Copied' : 'Copy'}</span>
              </button>
              <button
                onClick={handleDownload}
                className="p-1.5 text-xs text-[#aaa] hover:text-white bg-[#1a1a22] hover:bg-[#252530] border border-[#333] rounded-lg transition-colors"
                title="Download script"
              >
                <SafeIcon icon={FiDownload} className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={onClose}
                className="p-1.5 text-[#888] hover:text-white hover:bg-white/10 rounded-lg transition-colors"
              >
                <SafeIcon icon={FiX} className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Code Editor Area */}
          <div className="mt-4 flex-1 flex flex-col min-h-0">
            <div className="flex items-center justify-between text-xs text-[#777] mb-1.5 font-sans">
              <span>SANDBOX ENVIRONMENT</span>
              <span className="text-[10px] text-[#555]">Lines: {code.split('\n').length}</span>
            </div>
            <textarea
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="// Write or paste JavaScript code here...&#10;console.log('Sovereign Sandbox Active');"
              rows={9}
              className="w-full p-3.5 rounded-xl bg-[#0a0a0d] border border-[#222] text-emerald-400 text-xs font-mono resize-none focus:outline-none focus:border-emerald-500/50 shadow-inner"
              spellCheck="false"
            />

            {/* Run Action Bar */}
            <div className="flex items-center justify-between mt-3">
              <div className="flex items-center gap-2 text-xs">
                <button
                  onClick={handleRun}
                  className="flex items-center gap-2 px-4 py-2 bg-emerald-500 hover:bg-emerald-400 text-black font-semibold rounded-xl text-xs transition-all shadow-lg shadow-emerald-500/20 active:scale-95"
                >
                  <SafeIcon icon={FiPlay} className="w-3.5 h-3.5 fill-black" />
                  <span>Execute Code</span>
                </button>
                {duration !== null && (
                  <span className="text-[11px] text-[#888] font-mono">
                    ⏱️ {duration}ms
                  </span>
                )}
              </div>
              <button
                onClick={() => { setOutput([]); setDuration(null); setStatus('idle'); }}
                className="flex items-center gap-1 text-[11px] text-[#666] hover:text-[#999] transition-colors"
              >
                <SafeIcon icon={FiTrash2} className="w-3 h-3" />
                <span>Clear Output</span>
              </button>
            </div>

            {/* Output Terminal Console */}
            <div className="mt-3 flex-1 flex flex-col min-h-[120px] max-h-[160px] bg-[#070709] border border-[#1e1e24] rounded-xl p-3 overflow-y-auto">
              <div className="flex items-center gap-2 text-[10px] text-[#555] uppercase tracking-wider mb-1.5 pb-1 border-b border-[#181820]">
                <SafeIcon icon={FiTerminal} className="w-3 h-3 text-emerald-400" />
                <span>Sandbox Output Console</span>
                {status === 'success' && <span className="text-emerald-400 font-bold ml-auto">● SUCCESS</span>}
                {status === 'error' && <span className="text-rose-400 font-bold ml-auto">● ERROR</span>}
              </div>
              <div className="text-xs font-mono space-y-1">
                {output.length === 0 ? (
                  <span className="text-[#444] italic">Click "Execute Code" to evaluate in isolated sandbox.</span>
                ) : (
                  output.map((line, idx) => (
                    <div
                      key={idx}
                      className={
                        line.startsWith('[ERROR]')
                          ? 'text-rose-400'
                          : line.startsWith('[WARN]')
                          ? 'text-amber-400'
                          : 'text-emerald-300'
                      }
                    >
                      {line}
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
}
